import React, {useState, MouseEvent, FC, useEffect} from 'react';
import Box from '@material-ui/core/Box';
import {withStyles} from '@material-ui/core';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import {CODES} from './GameSelect';
import MultipleOption from './MultipleOption';
import AutocompleteList from './AutocompleteList';
import {AutocompleteListProps} from './types';
import {Option} from '../../types';
import {StyledPopover} from '../categorized-dropdown';
import PopperInput from '../common/PopperInput';
import {ITEM_DEFAULT_SIZE, VirtualizedListbox} from './VirtualizedListbox';

const StyledDropdown = withStyles(theme => ({
    root: {
        marginTop: theme.spacing(2),

        [theme.breakpoints.up('fmd')]: {
            width: '236px'
        }
    }
}))(Box);

export interface GameSelectWidgetProps extends AutocompleteListProps {
    options: Option[];
    value: Option[];
    supportedLocales: any;
    getMessage: (code: string, args?: string[] | number[]) => string;
}

const WidgetGameSelect: FC<GameSelectWidgetProps> = ({
    getMessage,
    supportedLocales,
    onChange,
    value,
    ...props
}: GameSelectWidgetProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [internalValue, setInternalValue] = useState(value);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);

        if (anchorEl) {
            onChange(event, internalValue);
        }
    };

    const handleChange = (_event, value: Option[]) => {
        setInternalValue(value);
    };

    const open = Boolean(anchorEl);

    useEffect(() => {
        setInternalValue(value);
    }, [value]);

    return (
        <>
            <PopperInput
                open={open}
                value={getMessage(CODES.selected, [internalValue.length])}
                onClick={handleClick}
            />
            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClick}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}>
                <StyledDropdown>
                    <AutocompleteList
                        {...props}
                        value={internalValue}
                        onChange={handleChange}
                        ListboxComponent={VirtualizedListbox}
                        ListboxProps={{
                            VariableSizeListProps: {
                                itemSize: () => ITEM_DEFAULT_SIZE
                            }
                        }}
                        resultsLabel={getMessage(CODES.searchResults)}
                        placeholder={getMessage(CODES.searchPlaceholder)}
                        noOptionsText={getMessage(CODES.noOptions)}
                        renderOption={({secondaryText, ...option}, {selected}) => (
                            <MultipleOption key={option.value} selected={selected} {...option} />
                        )}
                    />
                </StyledDropdown>
            </StyledPopover>
        </>
    );
};

export default withLocalization(WidgetGameSelect);
